import { useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import "./Login.css";
import { useAuth } from './AuthContext';
import App from './App';
import { postLogin } from './apiService';
import { jwtDecode } from 'jwt-decode';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  FaBuilding,
  FaUser,
  FaLock,
  FaEye,
  FaEyeSlash,
  FaShieldAlt,
  FaChartLine,
  FaUsers,
  FaTimes,
  FaCheck,
  FaSpinner
} from 'react-icons/fa';
import Logo from "./components/pages/Logo/CompanyLogo.png";

// Feature modal content data
const featureData = {
  security: {
    title: 'Secure Event Management',
    icon: FaShieldAlt,
    description: 'Enterprise-grade security to protect your event rental data',
    bullets: [
      'End-to-end encryption for all data transfers',
      'Role-based access control (RBAC)',
      'Multi-factor authentication support',
      'Regular automated backups',
      'Audit logs for all user activities',
      'GDPR and compliance ready'
    ]
  },
  analytics: {
    title: 'Real-time Event Analytics & Reports',
    icon: FaChartLine,
    description: 'Powerful insights to optimize your event rental business',
    bullets: [
      'Live dashboard with rental metrics',
      'Event performance analytics',
      'Equipment utilization reports',
      'Revenue and profit analysis',
      'Customer booking trends',
      'Export to PDF, Excel, and CSV'
    ]
  },
  multibranch: {
    title: 'Multi-location Operations',
    icon: FaUsers,
    description: 'Seamlessly manage event rentals across all your locations',
    bullets: [
      'Centralized control panel for all locations',
      'Location-wise inventory management',
      'Inter-location equipment transfers',
      'Consolidated event reports',
      'Location performance comparison',
      'Real-time equipment availability'
    ]
  }
};

const LoginForm = () => {
  const history = useHistory();
  const location = useLocation();
  const { isAuthenticated, login } = useAuth();

  // Branch validation states
  const [companyCode, setCompanyCode] = useState('');
  const [companyId, setCompanyId] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [branchId, setBranchId] = useState('');
  const [branchstate, setbranchstate] = useState('');
  const [companyCodeValidated, setCompanyCodeValidated] = useState(false);

  // Login states
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  // Modal state
  const [activeModal, setActiveModal] = useState(null);

  const openFeatureModal = (featureKey) => {
    setActiveModal(featureKey);
  };

  const closeFeatureModal = () => {
    setActiveModal(null);
  };

  // Handle branch code validation
  const handleCompanyValidation = async () => {
    if (!companyCode.trim()) {
      toast.error('Location code is required.', { position: 'top-center' });
      return;
    }

    setLoading(true);
    try {
      const CompanyData = { BranchCode: companyCode };
      const response = await postLogin('BranchLoginApi', CompanyData);

      if (response.responseCode === '1111') {
        setCompanyCodeValidated(true);
        setCompanyId(response.response_data.companyId);
        setBranchId(response.response_data.id);
        setCompanyName(response.response_data.name);
        setbranchstate(response.response_data.branchstate);
      } else {
        toast.error('Invalid Location code', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Location validation failed', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  // Handle login
  const handleLogin = async () => {
    if (!username.trim() || !password.trim()) {
      toast.error('Please enter username and password', { position: 'top-center' });
      return;
    }

    setLoading(true);
    try {
      const loginData = {
        UserName: username,
        Password: password,
        CompanyId: companyId,
        BranchId: branchId
      };

      const response = await postLogin('LoginApi', loginData);

      if (response.responseCode === '1111') {
        const jwtToken = response.response_data.token;
        const responseData = jwtDecode(jwtToken);

        const keysToRemove = ['iss', 'exp', 'ttp', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name', 'aud'];
        const permissions = Object.keys(responseData).filter(
          (key) => key !== 'companyId' && key !== 'userId' && !keysToRemove.includes(key)
        );

        const isSuper = responseData.isSuperAdmin?.toLowerCase() === "true";

        if (!isSuper) {
          const parsedCompanyId = parseInt(responseData.companyId, 10);
          if (parsedCompanyId !== companyId) {
            toast.error('Invalid User!', { position: 'top-center' });
            setLoading(false);
            return;
          }
        }

        login(
          responseData.companyId,
          responseData.branchId,
          responseData.userId,
          isSuper,
          responseData.superAdminCompanyId,
          companyName,
          permissions,
          jwtToken,
          responseData.roleName,
          branchstate,
          responseData.username
        );

        const redirectParam = new URLSearchParams(location.search).get('redirect');
        const redirectPath = redirectParam ? decodeURIComponent(redirectParam) : '/Home';
        history.replace(redirectPath);
      } else {
        toast.error('Invalid username or password', { position: 'top-center' });
      }
    } catch (error) {
      toast.error('Login failed', { position: 'top-center' });
    } finally {
      setLoading(false);
    }
  };

  if (isAuthenticated) {
    return <App />;
  }


  return (
    <div className="login-container">
      <ToastContainer />

      {/* Left Side - Branding */}
      <div className="login-left">
        <div className="branding-content">
          <div className="logo-container">
            <img src={Logo} alt="Event Rent Suite Logo" className="logo-icon-img" />
            <h3 className="brand-name">Event Rent Suite</h3>
          </div>
          <p className="brand-tagline">Event Rental Management Solution</p>

          <div className="feature-list">
            <div className="feature-item" onClick={() => openFeatureModal('security')}>
              <FaShieldAlt className="feature-icon" />
              <span>Secure Event Management</span>
            </div>
            <div className="feature-item" onClick={() => openFeatureModal('analytics')}>
              <FaChartLine className="feature-icon" />
              <span>Real-time Event Analytics & Reports</span>
            </div>
            <div className="feature-item" onClick={() => openFeatureModal('multibranch')}>
              <FaUsers className="feature-icon" />
              <span>Multi-location Operations</span>
            </div>
          </div>
        </div>

        <div className="branding-footer">
          <p>© 2025 Event Rent Suite. All rights reserved.</p>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="login-right">
        <div className="login-card">
          {/* Logo & Title */}
          <div className="glass-logo-container">
            <img src={Logo} alt="Login Logo" className="glass-logo" />
            <h6 className="glass-company-name" style={{ fontFamily: 'sans-serif' }}>{companyCodeValidated ? companyName : 'Event Rent Suite - ERS'}</h6>
          </div>

          {/* Branch Code Step */}
          {!companyCodeValidated ? (
            <form onSubmit={(e) => { e.preventDefault(); handleCompanyValidation(); }}>
              <div className="glass-input-group">
                <span className="glass-input-icon">
                  <FaBuilding />
                </span>
                <input
                  type="text"
                  placeholder="Enter Location Code"
                  value={companyCode}
                  onChange={(e) => setCompanyCode(e.target.value)}
                  className="glass-input"
                />
              </div>
              <button type="submit" disabled={loading} className="glass-btn">
                {loading && <FaSpinner className="spinner-icon" />}
                {loading ? 'Validating...' : 'Validate Location Code'}
              </button>
            </form>
          ) : (
            /* Username & Password Step */
            <form onSubmit={(e) => { e.preventDefault(); handleLogin(); }}>
              <p className="welcome-text" style={{ fontFamily: 'Times New Roman', fontSize: '13px' }}>Welcome to {companyName}</p>

              {/* Username */}
              <div className="glass-input-group">
                <span className="glass-input-icon">
                  <FaUser />
                </span>
                <input
                  type="text"
                  placeholder="User Name"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="glass-input"
                />
              </div>

              {/* Password */}
              <div className="glass-input-group">
                <span className="glass-input-icon">
                  <FaLock />
                </span>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="glass-input"
                />
                <div className="glass-eye-icon" onClick={() => setShowPassword((prev) => !prev)}>
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </div>
              </div>

              <button type="submit" disabled={loading} className="glass-btn">
                {loading && <FaSpinner className="spinner-icon" />}
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </form>
          )}
        </div>
      </div>

      {/* Feature Modal */}
      {activeModal && featureData[activeModal] && (
        <div className="feature-modal-overlay" onClick={closeFeatureModal}>
          <div className="feature-modal" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeFeatureModal}>
              <FaTimes />
            </button>
            <div className="modal-header">
              <div className="modal-header-row">
                <div className="modal-icon-wrapper">
                  {activeModal === 'security' && <FaShieldAlt className="modal-icon" />}
                  {activeModal === 'analytics' && <FaChartLine className="modal-icon" />}
                  {activeModal === 'multibranch' && <FaUsers className="modal-icon" />}
                </div>
                <h4 className="modal-title">{featureData[activeModal].title}</h4>
              </div>
              <p className="modal-description">{featureData[activeModal].description}</p>
            </div>
            <div className="modal-content">
              <ul className="feature-bullets">
                {featureData[activeModal].bullets.map((bullet, index) => (
                  <li key={index} className="feature-bullet">
                    <FaCheck className="bullet-icon" />
                    <span>{bullet}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LoginForm;